import React from 'react';
import { CalendarDays, TrendingUp, Percent, DollarSign, PiggyBank, AlertCircle, Landmark } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета сложных процентов
 */
const ResultsOverview = ({ results, formData }) => {
  if (!results) return null;

  return (
    <ToolResult title="Результаты расчета инвестиции">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Итоговая сумма */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Итоговая сумма
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.finalBalance)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Включает начальные инвестиции, дополнительные взносы и проценты
          </p>
        </div>

        {/* Чистый доход */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
          <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Чистый доход
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
              {formatCurrency(Math.abs(results.absoluteReturn))}
            </span>
            <span className="text-sm font-medium text-green-600 dark:text-green-400">
              (+{formatNumber(results.effectiveAnnualRate * 100, 2)}% годовых)
            </span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            Прибыль после вычета всех вложений, до налогообложения
          </p>
        </div>
      </div>

      {/* Суммы с учетом инфляции */}
      {formData.adjustForInflation && results.inflationRate > 0 && (
        <div className="p-4 mb-6 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
          <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2 flex items-center">
            <TrendingUp className="w-4 h-4 mr-2 text-yellow-600 dark:text-yellow-400" />
            С учетом инфляции ({formatNumber(results.inflationRate * 100, 2)}% в год)
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-800 p-3 rounded-md border border-yellow-100 dark:border-yellow-800/50">
              <p className="text-sm text-gray-600 dark:text-gray-400">Итоговая сумма в текущих ценах:</p>
              <p className="text-xl font-semibold text-yellow-700 dark:text-yellow-300">
                {formatCurrency(results.finalInflationAdjustedValue)}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-3 rounded-md border border-yellow-100 dark:border-yellow-800/50">
              <p className="text-sm text-gray-600 dark:text-gray-400">Реальная годовая доходность:</p>
              <p className="text-xl font-semibold text-yellow-700 dark:text-yellow-300">
                {formatNumber(results.effectiveRateInflationAdjusted * 100, 2)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Начальная сумма инвестиций" 
          value={formatCurrency(results.initialInvestment)} 
          color="indigo" 
          icon={<DollarSign className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Процентная ставка" 
          value={`${formatNumber(results.annualInterestRate * 100, 2)}% годовых`} 
          color="orange" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Срок инвестирования" 
          value={`${results.investmentYears} ${formatYearsLabel(results.investmentYears)}`} 
          icon={<CalendarDays className="w-4 h-4"/>} 
        />
        
        {results.totalContributions > results.initialInvestment && (
          <ResultItem 
            label="Общая сумма дополнительных взносов" 
            value={formatCurrency(results.totalContributions - results.initialInvestment)} 
            color="green"
            icon={<PiggyBank className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Начисленные проценты (до налогов)" 
          value={formatCurrency(results.totalInterestEarned)} 
          color="green"
        />
        
        {results.totalTaxesPaid > 0 && (
          <ResultItem 
            label="Уплаченные налоги" 
            value={formatCurrency(results.totalTaxesPaid)} 
            color="red" 
            icon={<Landmark className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Среднегодовой доход" 
          value={formatCurrency(results.averageAnnualReturn)} 
        />
        
        <ResultItem 
          label="Эффективная годовая доходность" 
          value={`${formatNumber(results.effectiveAnnualRate * 100, 2)}%`} 
          color="orange" 
        />
        
        {formData.adjustForInflation && results.inflationRate > 0 && (
          <ResultItem 
            label="Реальная годовая доходность (с учетом инфляции)" 
            value={`${formatNumber(results.effectiveRateInflationAdjusted * 100, 2)}%`} 
            color="yellow" 
          />
        )}
      </div>

      {/* Информация о сложных процентах */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <AlertCircle className="w-5 h-5 text-indigo-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Сила сложных процентов</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Благодаря капитализации процентов, ваша прибыль составила {formatCurrency(results.absoluteReturn)}, 
              что на {formatNumber((results.absoluteReturn / (results.totalContributions - results.initialInvestment || 1)) * 100, 0)}% 
              больше, чем вы бы получили при простых процентах. Регулярные инвестиции и долгосрочный горизонт 
              максимизируют эффект капитализации.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ResultsOverview;